import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Card } from "./ui/card";
import { Globe as GlobeIcon, Activity } from "lucide-react";
import api from "../lib/api";

const V_COLOR = {
    "true":         "#22e0b4", "mostly_true":  "#7cf5c8",
    "misleading":   "#f5b942", "false":        "#ff6b6b",
    "unverified":   "#8890c9",
    "safe":         "#22e0b4", "likely_safe":  "#7cf5c8",
    "suspicious":   "#f5b942", "scam":         "#ff6b6b",
};

/**
 * TruthGlobe — rotating SVG hemisphere with glowing dots for the most-recent
 * verdicts. Not a real geographic globe (we don't collect claim geolocation);
 * dots are placed deterministically from check_id hash so they feel "real"
 * without pretending to be map coordinates.
 */
export default function TruthGlobe() {
    const [items, setItems] = useState([]);

    useEffect(() => {
        let alive = true;
        const load = () =>
            api.get("/trending").then((r) => {
                if (!alive) return;
                setItems((r.data || []).slice(0, 24));
            }).catch(() => {});
        load();
        const t = setInterval(load, 30_000);
        return () => { alive = false; clearInterval(t); };
    }, []);

    const dots = useMemo(() => {
        return items.map((it) => {
            const h = hashCode(it.check_id || "x");
            // Convert to lat/lng in the visible hemisphere (front-facing)
            const lat = ((h % 140) - 70);       // -70 to 70 degrees
            const lng = (((h >> 4) % 160) - 80); // -80 to 80 degrees
            return {
                id: it.check_id,
                lat, lng,
                color: V_COLOR[it.verdict] || "#8890c9",
                verdict: it.verdict,
                claim: it.claim || "",
            };
        });
    }, [items]);

    const counts = useMemo(() => {
        const c = { true: 0, mostly_true: 0, misleading: 0, false: 0, unverified: 0, scam: 0 };
        items.forEach((it) => { if (it.verdict in c) c[it.verdict] += 1; });
        return c;
    }, [items]);

    return (
        <section
            className="relative max-w-6xl mx-auto px-6 lg:px-10 py-20"
            data-testid="truth-globe"
            aria-labelledby="truth-globe-heading"
        >
            <div className="pointer-events-none absolute inset-x-0 top-24 h-72 bg-primary/10 blur-[80px] -z-0" />

            <div className="relative z-10 grid lg:grid-cols-5 gap-10 items-center">
                {/* LEFT — Globe */}
                <div className="lg:col-span-3 relative flex items-center justify-center">
                    <GlobeSvg dots={dots} />
                </div>

                {/* RIGHT — narrative + legend */}
                <div className="lg:col-span-2">
                    <div className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/15 px-3 py-1 text-[11px] font-semibold text-accent mb-4">
                        <GlobeIcon className="h-3 w-3 text-primary" /> Truth-Globe
                    </div>
                    <h2 id="truth-globe-heading" className="font-display text-3xl md:text-4xl text-accent tracking-tight leading-[1.05]">
                        The world&#39;s claims — <span className="text-primary">checked in real time</span>.
                    </h2>
                    <p className="mt-4 text-muted-foreground leading-relaxed">
                        Every dot is a recent verdict from the Real-Check pipeline.
                        Green = confirmed. Red = false or scam. Amber = misleading.
                        Watch the truth take shape as it happens.
                    </p>
                    <div className="mt-6 grid grid-cols-2 gap-2">
                        <LegendRow color={V_COLOR.true}       label="True"       count={counts.true + counts.mostly_true} />
                        <LegendRow color={V_COLOR.misleading} label="Misleading" count={counts.misleading} />
                        <LegendRow color={V_COLOR.false}      label="False"      count={counts.false + counts.scam} />
                        <LegendRow color={V_COLOR.unverified} label="Unverified" count={counts.unverified} />
                    </div>
                    <p className="text-xs text-muted-foreground mt-4 flex items-center gap-1.5">
                        <Activity className="h-3 w-3 text-emerald-400 animate-pulse" />
                        Live · updates every 30s
                    </p>
                </div>
            </div>
        </section>
    );
}

/** Hash → deterministic int for dot placement */
function hashCode(str) {
    let h = 5381;
    for (let i = 0; i < str.length; i++) {
        h = ((h << 5) + h + str.charCodeAt(i)) & 0xffffffff;
    }
    return Math.abs(h);
}

/** Convert lat/lng → 2-D projection onto a rotating sphere.
 *  The visible sphere spans radius=R. rotAngle rotates the sphere around Y. */
function latLngToXY(lat, lng, R, rotAngle) {
    const latRad = (lat * Math.PI) / 180;
    const lngRad = ((lng + rotAngle) * Math.PI) / 180;
    const x = R * Math.cos(latRad) * Math.sin(lngRad);
    const y = -R * Math.sin(latRad);
    const z = R * Math.cos(latRad) * Math.cos(lngRad);
    return { x, y, z, visible: z >= 0 };
}

function GlobeSvg({ dots }) {
    const [rot, setRot] = useState(0);
    // Continuous rotation animation
    useEffect(() => {
        let raf, last;
        const step = (t) => {
            if (last === undefined) last = t;
            const dt = (t - last) / 1000;
            last = t;
            setRot((r) => (r + dt * 8) % 360); // 8°/sec, 45s per full rotation
            raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
        return () => cancelAnimationFrame(raf);
    }, []);

    const size = 460;
    const R = 190;
    const cx = size / 2;
    const cy = size / 2;

    // Meridian lines
    const meridians = Array.from({ length: 8 }, (_, i) => i * 45);
    const parallels = [-60, -30, 0, 30, 60];

    return (
        <div className="relative" style={{ width: size, height: size }}>
            {/* outer soft glow */}
            <div
                className="absolute inset-0 rounded-full pointer-events-none"
                style={{
                    background: "radial-gradient(circle at 50% 50%, rgba(34,224,180,0.15) 0%, transparent 60%)",
                    filter: "blur(20px)",
                }}
            />
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="relative">
                <defs>
                    <radialGradient id="rc-globe-fill" cx="35%" cy="35%">
                        <stop offset="0%" stopColor="#0e3d33" stopOpacity="0.9" />
                        <stop offset="70%" stopColor="#062024" stopOpacity="0.95" />
                        <stop offset="100%" stopColor="#020a0b" stopOpacity="1" />
                    </radialGradient>
                    <linearGradient id="rc-globe-highlight" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="rgba(255,255,255,0.35)" />
                        <stop offset="100%" stopColor="rgba(255,255,255,0)" />
                    </linearGradient>
                </defs>

                {/* Sphere base */}
                <circle cx={cx} cy={cy} r={R} fill="url(#rc-globe-fill)" stroke="rgba(34,224,180,0.35)" strokeWidth="1" />
                {/* Highlight (glossy top-left) */}
                <ellipse cx={cx - 40} cy={cy - 55} rx={R * 0.55} ry={R * 0.35} fill="url(#rc-globe-highlight)" opacity="0.35" />

                {/* Parallels */}
                {parallels.map((lat) => {
                    const y = cy - R * Math.sin((lat * Math.PI) / 180);
                    const rx = R * Math.cos((lat * Math.PI) / 180);
                    return (
                        <ellipse
                            key={`p${lat}`}
                            cx={cx} cy={y}
                            rx={rx} ry={rx * 0.25}
                            fill="none"
                            stroke="rgba(34,224,180,0.18)"
                            strokeWidth="0.8"
                        />
                    );
                })}

                {/* Meridians — rendered as vertical ellipses skewed by rotation */}
                {meridians.map((m) => {
                    const angle = ((m + rot) % 360) - 180;
                    const rx = Math.abs(R * Math.sin((angle * Math.PI) / 180));
                    const opacity = Math.abs(Math.cos((angle * Math.PI) / 180)) * 0.4 + 0.05;
                    return (
                        <ellipse
                            key={`m${m}`}
                            cx={cx} cy={cy}
                            rx={rx} ry={R}
                            fill="none"
                            stroke="rgba(34,224,180,0.35)"
                            strokeWidth="0.7"
                            opacity={opacity}
                        />
                    );
                })}

                {/* Dots — recent verdicts */}
                {dots.map((d, i) => {
                    const p = latLngToXY(d.lat, d.lng, R, rot);
                    if (!p.visible) return null;
                    const depth = (p.z + R) / (2 * R); // 0 back → 1 front
                    const opacity = 0.4 + depth * 0.6;
                    const radius = 2 + depth * 3.5;
                    return (
                        <g key={`${d.id}-${i}`} style={{ transformOrigin: `${cx + p.x}px ${cy + p.y}px` }}>
                            <circle
                                cx={cx + p.x}
                                cy={cy + p.y}
                                r={radius + 4}
                                fill={d.color}
                                opacity={opacity * 0.15}
                            />
                            <circle
                                cx={cx + p.x}
                                cy={cy + p.y}
                                r={radius}
                                fill={d.color}
                                opacity={opacity}
                                style={{ filter: `drop-shadow(0 0 6px ${d.color})` }}
                            >
                                <animate
                                    attributeName="r"
                                    values={`${radius};${radius + 1.5};${radius}`}
                                    dur={`${2 + (i % 3)}s`}
                                    repeatCount="indefinite"
                                />
                            </circle>
                        </g>
                    );
                })}

                {/* Center compass emblem */}
                <g opacity="0.5">
                    <circle cx={cx} cy={cy} r="18" fill="none" stroke="rgba(34,224,180,0.4)" strokeWidth="1" />
                    <circle cx={cx} cy={cy} r="3" fill="#22e0b4" />
                </g>
            </svg>
        </div>
    );
}

function LegendRow({ color, label, count }) {
    return (
        <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-white/5 border border-white/10" data-testid={`globe-legend-${label.toLowerCase()}`}>
            <span className="flex items-center gap-2">
                <span
                    className="w-2 h-2 rounded-full shrink-0"
                    style={{ background: color, boxShadow: `0 0 8px ${color}` }}
                />
                <span className="text-sm text-muted-foreground">{label}</span>
            </span>
            <span className="text-sm font-mono text-accent tabular-nums">{count}</span>
        </div>
    );
}
